import { getInlineStyles } from './styles.js';
import { createIframe } from './iframe.js';
import type { CalAttrs } from './types.js';

/**
 * Inline embed — replaces element's content with a Shadow DOM + iframe.
 * The host element must have an explicit height (e.g. style="height:680px").
 */

export interface InlineOptions {
  container: Element;
  iframeSrc: string;
  calOrigin: string;
  attrs: CalAttrs;
  onBooked?: (bookingId: string) => void;
}

export function mountInline(opts: InlineOptions): () => void {
  const { container, iframeSrc, calOrigin, attrs, onBooked } = opts;

  // Mark as mounted to avoid double-mounting
  if (container.hasAttribute('data-cal-mounted')) return () => undefined;
  container.setAttribute('data-cal-mounted', '');

  const shadow = (container as HTMLElement).attachShadow({ mode: 'open' });

  const styleEl = document.createElement('style');
  styleEl.textContent = getInlineStyles();
  shadow.appendChild(styleEl);

  const wrap = document.createElement('div');
  wrap.className = 'cal-inline-wrap';
  shadow.appendChild(wrap);

  const iframeHandle = createIframe({
    src: iframeSrc,
    calOrigin,
    attrs,
    onResize(height) {
      // In inline mode, resize the container if it has auto height
      const containerEl = container as HTMLElement;
      if (!containerEl.style.height || containerEl.style.height === 'auto') {
        containerEl.style.height = `${height}px`;
      }
    },
    onBooked(bookingId) {
      onBooked?.(bookingId);
    },
  });

  wrap.appendChild(iframeHandle.wrapper);

  return () => {
    iframeHandle.destroy();
    container.removeAttribute('data-cal-mounted');
  };
}
